import React, { useState, useEffect } from 'react';
import StudentCard from '../components/StudentCard';
import MatchResult from '../components/MatchResult';
import ThinkingDisplay from '../components/ThinkingDisplay';
import BatchMatchResults from '../components/BatchMatchResults';
import GroupMatchResults from '../components/GroupMatchResults';
import GroupMatchProgress from '../components/GroupMatchProgress';
import { apiFetch, createStreamFetch } from '../api';
import toast from '../utils/toast';
import '../styles/demo.css';

const DemoMatchmaker = () => {
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState(new Set());
  const [matches, setMatches] = useState([]);
  const [batchResults, setBatchResults] = useState(null);
  const [groupMatchResults, setGroupMatchResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [isThinking, setIsThinking] = useState(false);
  const [thinkingMessage, setThinkingMessage] = useState('');
  const [streamingText, setStreamingText] = useState('');
  const [matchMode, setMatchMode] = useState('single'); // 'single' | 'batch' | 'group'

  // Group matching progress state
  const [groupProgress, setGroupProgress] = useState(0);
  const [currentTutor, setCurrentTutor] = useState(null);
  const [currentStudent, setCurrentStudent] = useState(null);
  const [completedTutors, setCompletedTutors] = useState([]);
  const [totalTutors, setTotalTutors] = useState(0);
  const [totalStudentsInGroup, setTotalStudentsInGroup] = useState(0);
  // Cost is tracked globally via App header counter

  // Fetch all students on component mount
  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    setStudentsLoading(true);
    setError(null);
    try {
      const response = await apiFetch('/api/demo/students');
      if (!response.ok) {
        throw new Error('Failed to fetch students');
      }
      const data = await response.json();
      setStudents(data.students || []);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching students:', err);
    } finally {
      setStudentsLoading(false);
    }
  };

  const handleStudentClick = async (student) => {
    // In batch or group mode, toggle selection
    if (matchMode === 'batch' || matchMode === 'group') {
      setSelectedStudents(prev => {
        const newSet = new Set(prev);
        if (newSet.has(student.uuid)) {
          newSet.delete(student.uuid);
        } else {
          newSet.add(student.uuid);
        }
        return newSet;
      });
      return;
    }

    // Single student mode (original behavior)
    if (selectedStudent?.uuid === student.uuid) {
      // Deselect if clicking the same student
      setSelectedStudent(null);
      setMatches([]);
      setIsThinking(false);
      setThinkingMessage('');
      setStreamingText('');
      return;
    }

    setSelectedStudent(student);
    setMatches([]);
    setBatchResults(null);
    setGroupMatchResults(null);
    setLoading(true);
    setError(null);
    setIsThinking(false);
    setThinkingMessage('');
    setStreamingText('');
    // Cost is tracked globally; no local counter here

    try {
      // Use streaming endpoint with centralized auth and 429 handling
      const response = await createStreamFetch('/api/demo/match-student-stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_uuid: student.uuid,
        }),
      });

      // Handle 429 rate limit - toast already shown by createStreamFetch
      if (response.status === 429) {
        setError('Rate limit reached. Please wait and try again.');
        setLoading(false);
        setIsThinking(false);
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to match student');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          setLoading(false);
          setIsThinking(false);
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop(); // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const event = JSON.parse(line.slice(6));

              switch (event.type) {
                case 'thinking_start':
                  setIsThinking(true);
                  setThinkingMessage(event.data.message || 'Thinking...');
                  break;

                case 'thinking_end':
                  setThinkingMessage(event.data.message || 'Analysis complete');
                  setTimeout(() => setIsThinking(false), 1000);
                  break;

                case 'text_delta':
                  setStreamingText(event.data.accumulated || '');
                  break;

                case 'matches_complete':
                  setMatches(event.data.matches || []);
                  // Update cost counter if backend provided cost_data
                  if (event.data && event.data.cost_data) {
                    const cost = event.data.cost_data.cost || event.data.cost_data.total_cost;
                    if (typeof cost === 'number' && cost > 0) {
                      try {
                        window.dispatchEvent(new CustomEvent('lrbrain-cost-update', { detail: { cost } }));
                      } catch (e) {
                        console.warn('Failed to dispatch cost update event', e);
                      }
                    }
                  }
                  setLoading(false);
                  setIsThinking(false);
                  setStreamingText('');
                  break;

                case 'error':
                  // Check if this is a rate limit error from the backend
                  if (event.data.status === 429 || event.data.code === 'RATE_LIMITED' ||
                      (event.data.error && event.data.error.toLowerCase().includes('rate limit'))) {
                    toast.rateLimit(event.data.retry_after || null);
                    setError('Rate limit reached. Please wait and try again.');
                  } else {
                    setError(event.data.error || 'An error occurred');
                  }
                  setLoading(false);
                  setIsThinking(false);
                  break;

                case 'done':
                  setLoading(false);
                  setIsThinking(false);
                  break;

                default:
                  console.log('Unknown event type:', event.type);
              }
            } catch (e) {
              console.error('Error parsing SSE event:', e);
            }
          }
        }
      }
    } catch (err) {
      setError(err.message);
      console.error('Error matching student:', err);
      setLoading(false);
      setIsThinking(false);
    }
  };

  const handleBatchMatch = async () => {
    const uuidsToMatch = selectedStudents.size > 0
      ? Array.from(selectedStudents)
      : []; // Empty array means match ALL students

    setLoading(true);
    setError(null);
    setBatchResults(null);
    setGroupMatchResults(null);
    setMatches([]);
    setSelectedStudent(null);

    try {
      const response = await apiFetch('/api/demo/match-students-batch', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          student_uuids: uuidsToMatch,
          top_n: 5,
          persist: true
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to run batch matching');
      }

      const data = await response.json();
      setBatchResults(data);

      // Update cost counter if backend provided cost_data
      if (data.cost_data) {
        const cost = data.cost_data.cost || data.cost_data.total_cost;
        if (typeof cost === 'number' && cost > 0) {
          try {
            window.dispatchEvent(new CustomEvent('lrbrain-cost-update', { detail: { cost } }));
          } catch (e) {
            console.warn('Failed to dispatch cost update event', e);
          }
        }
      }
    } catch (err) {
      setError(err.message);
      console.error('Error in batch matching:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateMatchStatus = async (matchId, newStatus) => {
    try {
      const response = await apiFetch(`/api/demo/matches/${matchId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        throw new Error('Failed to update match status');
      }

      // Optionally refresh batch results or show success message
      console.log(`Match ${matchId} status updated to ${newStatus}`);
    } catch (err) {
      console.error('Error updating match status:', err);
      setError(err.message);
    }
  };

  const changeMode = (newMode) => {
    if (newMode === matchMode) return;
    setMatchMode(newMode);
    setSelectedStudents(new Set());
    setSelectedStudent(null);
    setMatches([]);
    setBatchResults(null);
    setGroupMatchResults(null);
    setError(null);
  };

  const selectAllStudents = () => {
    setSelectedStudents(new Set(students.map(s => s.uuid)));
  };

  const clearSelection = () => {
    setSelectedStudents(new Set());
  };

  const handleGroupMatch = async () => {
    const uuidsToMatch = Array.from(selectedStudents);

    if (uuidsToMatch.length < 2) {
      setError('Select at least 2 students for group matching');
      return;
    }

    setLoading(true);
    setError(null);
    setGroupMatchResults(null);
    setBatchResults(null);
    setMatches([]);
    setSelectedStudent(null);

    // Reset group progress state
    setGroupProgress(0);
    setCurrentTutor(null);
    setCurrentStudent(null);
    setCompletedTutors([]);
    setTotalTutors(0);
    setTotalStudentsInGroup(uuidsToMatch.length);

    try {
      // Use streaming endpoint with centralized auth and 429 handling
      const response = await createStreamFetch('/api/demo/match-group-stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_uuids: uuidsToMatch,
          top_n: 5
        }),
      });

      // Handle 429 rate limit - toast already shown by createStreamFetch
      if (response.status === 429) {
        setError('Rate limit reached. Please wait and try again.');
        setLoading(false);
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to run group matching');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          setLoading(false);
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop(); // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const event = JSON.parse(line.slice(6));

              switch (event.type) {
                case 'progress_start':
                  setTotalTutors(event.data.total_tutors);
                  setTotalStudentsInGroup(event.data.group_size);
                  break;

                case 'evaluating_tutor':
                  setCurrentTutor({
                    name: event.data.tutor_name,
                    headshot: event.data.tutor_headshot,
                    index: event.data.tutor_index
                  });
                  break;

                case 'student_score':
                  setCurrentStudent({
                    grade: event.data.student_grade,
                    subjects: event.data.student_subjects,
                    score: event.data.score
                  });
                  break;

                case 'tutor_complete':
                  setCompletedTutors(prev => [...prev, {
                    tutor_id: event.data.tutor_id,
                    tutor_name: event.data.tutor_name,
                    group_score: event.data.group_score,
                    rank_so_far: event.data.rank_so_far,
                    tutor_headshot: event.data.tutor_headshot
                  }]);
                  setCurrentStudent(null);
                  break;

                case 'progress_update':
                  setGroupProgress(event.data.progress);
                  break;

                case 'matches_complete':
                  setGroupMatchResults(event.data);
                  // Update cost counter if backend provided cost_data
                  if (event.data.cost_data) {
                    const cost = event.data.cost_data.cost || event.data.cost_data.total_cost;
                    if (typeof cost === 'number' && cost > 0) {
                      try {
                        window.dispatchEvent(new CustomEvent('lrbrain-cost-update', { detail: { cost } }));
                      } catch (e) {
                        console.warn('Failed to dispatch cost update event', e);
                      }
                    }
                  }
                  setLoading(false);
                  // Reset progress state
                  setCurrentTutor(null);
                  setCurrentStudent(null);
                  break;

                case 'error':
                  // Check if this is a rate limit error from the backend
                  if (event.data.status === 429 || event.data.code === 'RATE_LIMITED' ||
                      (event.data.error && event.data.error.toLowerCase().includes('rate limit'))) {
                    toast.rateLimit(event.data.retry_after || null);
                    setError('Rate limit reached. Please wait and try again.');
                  } else {
                    setError(event.data.error || 'An error occurred');
                  }
                  setLoading(false);
                  break;

                case 'done':
                  setLoading(false);
                  break;

                default:
                  console.log('Unknown event type:', event.type);
              }
            } catch (e) {
              console.error('Error parsing SSE event:', e);
            }
          }
        }
      }
    } catch (err) {
      setError(err.message);
      console.error('Error in group matching:', err);
      setLoading(false);
    }
  };

  const getModeDescription = () => {
    switch (matchMode) {
      case 'batch':
        return 'Select multiple students for batch matching, or match all at once.';
      case 'group':
        return 'Select 2+ students to find the best single tutor for the entire group.';
      default:
        return 'Select a student to see their AI-matched tutors.';
    }
  };

  return (
    <div className="demo-matchmaker">
      <header className="demo-header">
        <h1>Tutor Matchmaker Demo</h1>
        <p>{getModeDescription()}</p>
        <div className="mode-toggle">
          <button
            className={`mode-btn ${matchMode === 'single' ? 'active' : ''}`}
            onClick={() => changeMode('single')}
          >
            Single Match
          </button>
          <button
            className={`mode-btn ${matchMode === 'batch' ? 'active' : ''}`}
            onClick={() => changeMode('batch')}
          >
            Batch Match
          </button>
          <button
            className={`mode-btn group-mode-btn ${matchMode === 'group' ? 'active' : ''}`}
            onClick={() => changeMode('group')}
          >
            Group Match
          </button>
        </div>
      </header>

      <div className="demo-content">
        {/* Left Panel - Students */}
        <div className="students-panel">
          <div className="students-header">
            <h2>Students</h2>
            {(matchMode === 'batch' || matchMode === 'group') && students.length > 0 && (
              <div className="batch-controls">
                <button className="batch-action-btn" onClick={selectAllStudents}>
                  Select All
                </button>
                <button className="batch-action-btn" onClick={clearSelection}>
                  Clear
                </button>
                <span className="selection-count">
                  {selectedStudents.size} selected
                </span>
              </div>
            )}
          </div>

          {studentsLoading && (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading students...</p>
            </div>
          )}

          {!studentsLoading && error && !batchResults && !groupMatchResults && (
            <div className="error-state">
              <p>Error: {error}</p>
              <button onClick={fetchStudents}>Retry</button>
            </div>
          )}

          {!studentsLoading && !error && students.length === 0 && (
            <div className="empty-state">
              <p>No students found</p>
            </div>
          )}

          <div className="students-grid">
            {students.map((student) => (
              <StudentCard
                key={student.uuid}
                student={student}
                isSelected={matchMode !== 'single' ? selectedStudents.has(student.uuid) : selectedStudent?.uuid === student.uuid}
                onClick={() => handleStudentClick(student)}
              />
            ))}
          </div>

          {matchMode === 'batch' && (
            <div className="batch-match-trigger">
              <button
                className="batch-match-btn"
                onClick={handleBatchMatch}
                disabled={loading}
              >
                {loading ? 'Matching...' : (
                  selectedStudents.size > 0
                    ? `Match ${selectedStudents.size} Student${selectedStudents.size !== 1 ? 's' : ''}`
                    : 'Match All Students'
                )}
              </button>
            </div>
          )}

          {matchMode === 'group' && (
            <div className="batch-match-trigger">
              <button
                className="batch-match-btn group-match-btn"
                onClick={handleGroupMatch}
                disabled={loading || selectedStudents.size < 2}
              >
                {loading ? 'Finding group tutor...' : (
                  `Find Tutor for ${selectedStudents.size} Student${selectedStudents.size !== 1 ? 's' : ''}`
                )}
              </button>
              {selectedStudents.size < 2 && (
                <p className="group-hint">Select at least 2 students for group matching</p>
              )}
            </div>
          )}
        </div>

        {/* Right Panel - Matches */}
        <div className="matches-panel">
          {!selectedStudent && !loading && !batchResults && !groupMatchResults && (
            <div className="welcome-state">
              <div className="welcome-icon">
                {matchMode === 'group' ? '👥' : matchMode === 'batch' ? '📋' : '👈'}
              </div>
              <h2>Welcome to the Matchmaker</h2>
              {matchMode === 'group' ? (
                <>
                  <p>Select 2 or more students to find the best single tutor for the entire group.</p>
                  <p className="welcome-hint">
                    Group matching uses <strong>minimum score logic</strong> - the tutor's group score equals
                    the lowest individual student score, ensuring the tutor works well for everyone.
                  </p>
                </>
              ) : matchMode === 'batch' ? (
                <>
                  <p>Select students and click "Match" to find tutors for multiple students at once.</p>
                  <p className="welcome-hint">
                    Batch matching evaluates all available tutors against each selected student,
                    respecting tutor capacity limits.
                  </p>
                </>
              ) : (
                <>
                  <p>Click on a student card to discover their perfect tutor matches.</p>
                  <p className="welcome-hint">
                    Our AI analyzes subject expertise, learning styles, personality fit, and more
                    to find the best tutors for each student.
                  </p>
                </>
              )}
            </div>
          )}

          {loading && matchMode === 'group' && (
            <GroupMatchProgress
              progress={groupProgress}
              currentTutor={currentTutor}
              currentStudent={currentStudent}
              completedTutors={completedTutors}
              totalTutors={totalTutors}
              totalStudents={totalStudentsInGroup}
              groupSize={totalStudentsInGroup}
            />
          )}

          {loading && matchMode !== 'group' && (
            <div className="loading-state">
              <ThinkingDisplay
                isThinking={isThinking}
                message={thinkingMessage}
                textDelta={streamingText}
              />
              <div className="spinner large"></div>
              <h3>
                {matchMode === 'batch'
                  ? 'Running batch matching...'
                  : 'Finding perfect matches...'}
              </h3>
              <p>Our AI is analyzing tutor profiles</p>
            </div>
          )}

          {error && !loading && (selectedStudent || matchMode !== 'single') && !batchResults && !groupMatchResults && (
            <div className="error-state">
              <h3>Oops!</h3>
              <p>Error: {error}</p>
              {selectedStudent && (
                <button onClick={() => handleStudentClick(selectedStudent)}>
                  Try Again
                </button>
              )}
            </div>
          )}

          {/* Single student matches */}
          {!loading && !error && matches.length > 0 && !batchResults && !groupMatchResults && (
            <div className="matches-container">
              <h2>Top Matches</h2>
              <p className="matches-subtitle">
                Found {matches.length} excellent tutor{matches.length !== 1 ? 's' : ''} for this student
              </p>
              <div className="matches-list">
                {matches.map((match, index) => (
                  <MatchResult
                    key={match.tutor_id}
                    match={match}
                    rank={index + 1}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Batch match results */}
          {!loading && batchResults && (
            <BatchMatchResults
              batchResults={batchResults}
              onUpdateMatchStatus={handleUpdateMatchStatus}
            />
          )}

          {/* Group match results */}
          {!loading && groupMatchResults && (
            <GroupMatchResults groupResults={groupMatchResults} />
          )}

          {!loading && !error && selectedStudent && matches.length === 0 && !batchResults && !groupMatchResults && (
            <div className="empty-state">
              <h3>No matches found</h3>
              <p>We couldn't find any suitable tutors for this student.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DemoMatchmaker;
